import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { Toaster } from '@/components/ui/sonner';
import { LoginPage } from './pages/LoginPage';
import { LandingPage } from './pages/LandingPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AdminPage } from './pages/AdminPage';
import { DashboardPage } from './pages/DashboardPage';
import { NewProjectPage } from './pages/NewProjectPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { TestCaseDetailPage } from './pages/TestCaseDetailPage';
import { TestRunExecutionPage } from './pages/TestRunExecutionPage';
import { PricingPage } from './pages/PricingPage';
import { useEffect } from 'react';
import { trackFunnel } from './api';
import { SEOCanonical } from './SEOCanonical';
import './App.css';

// Lightweight visitor tracking
const VisitorTracker = () => {
  const location = useLocation();
  useEffect(() => {
    let vid = localStorage.getItem('tg_vid');
    if (!vid) { vid = crypto.randomUUID?.() || Math.random().toString(36).slice(2); localStorage.setItem('tg_vid', vid); }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track-visit`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitor_id: vid, page: location.pathname })
    }).catch(() => {});
    // Funnel: landing_visit (anonymous, only for top-of-funnel pages — landing/login/signup)
    if (['/', '/login', '/signup'].includes(location.pathname)) {
      trackFunnel('landing_visit', { page: location.pathname });
    }
  }, [location.pathname]);
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-center">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-center">Loading...</div></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <VisitorTracker />
        <SEOCanonical />
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/projects/new" element={<ProtectedRoute><NewProjectPage /></ProtectedRoute>} />
          <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
          <Route path="/testcases/:testcaseId" element={<ProtectedRoute><TestCaseDetailPage /></ProtectedRoute>} />
          <Route path="/testruns/:testRunId" element={<ProtectedRoute><TestRunExecutionPage /></ProtectedRoute>} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;